import { fileEditDurableBody, fileEditJournalKey, validateFileEditJournal, verifyFileEditReply, type FileEditJournal, type FileEditJournalBody } from "@/lib/fileEditProof";

const listeners = new Set<(ownerId:string) => void>();
export function notifyFileEditJournalChanged(ownerId:string) { listeners.forEach(listener=>listener(ownerId)); }
export function subscribeFileEditJournalChanged(listener:(ownerId:string)=>void) { listeners.add(listener);return()=>{listeners.delete(listener);}; }
/** A separate namespace prevents old append/create journals becoming edit authority. */
async function journal<T>(mode:IDBTransactionMode,work:(store:IDBObjectStore,finish:(result:T)=>void)=>void):Promise<T> {
  const db=await new Promise<IDBDatabase>((resolve,reject)=>{
    const request=indexedDB.open("vyv-connected-file-edits",1);
    request.onupgradeneeded=()=>{const store=request.result.createObjectStore("edits",{keyPath:"key"});store.createIndex("ownerSession","ownerSession",{unique:true});};
    request.onsuccess=()=>resolve(request.result);
    request.onerror=request.onblocked=()=>reject(new Error("pending_storage_unavailable"));
  });
  return new Promise<T>((resolve,reject)=>{
    const tx=db.transaction("edits",mode);let result:T;
    tx.oncomplete=()=>{db.close();resolve(result);};
    tx.onerror=tx.onabort=()=>{db.close();reject(new Error("pending_storage_unavailable"));};
    work(tx.objectStore("edits"),value=>{result=value;});
  });
}
export async function loadFileEditJournal(owner:string,sid:string):Promise<FileEditJournal|null> {
  return journal("readonly",(store,finish)=>{
    const request=store.index("ownerSession").get(owner+":"+sid);
    request.onsuccess=()=>{
      const entry=request.result;if(!entry){finish(null);return;}
      if(!validateFileEditJournal(entry,owner,sid)){store.transaction.abort();return;}
      // Durable success is only a locator. Fresh provider proof must be looked up.
      const {confirmedResult:_proof,...durable}=entry;finish({...durable,status:"pending"});
    };
  });
}
export async function claimFileEditJournal(owner:string,sid:string,body:FileEditJournalBody,isCurrent:()=>boolean):Promise<{entry:FileEditJournal;created:boolean}> {
  const entry:FileEditJournal={key:fileEditJournalKey(owner,sid,body.idempotency_key),ownerSession:owner+":"+sid,ownerId:owner,authSessionId:sid,body:fileEditDurableBody(body),status:"pending"};
  if(!validateFileEditJournal(entry,owner,sid)||!isCurrent())throw new Error("invalid_session");
  return journal("readwrite",(store,finish)=>{
    const request=store.index("ownerSession").get(entry.ownerSession);
    request.onsuccess=()=>{
      if(!isCurrent()){store.transaction.abort();return;}
      if(request.result){
        if(!validateFileEditJournal(request.result,owner,sid)){store.transaction.abort();return;}
        const {confirmedResult:_proof,...durable}=request.result;
        finish({entry:{...durable,status:"pending"},created:false});return;
      }
      store.add(entry);finish({entry,created:true});
    };
  });
}
export async function finishFileEditJournal(entry:FileEditJournal,remove:boolean,isCurrent:()=>boolean):Promise<void> {
  if(!validateFileEditJournal(entry,entry.ownerId,entry.authSessionId)||!isCurrent())throw new Error("invalid_session");
  if(!remove&&(!entry.confirmedResult||!await verifyFileEditReply(entry.confirmedResult,entry.body,entry.confirmedResult.phase)))throw new Error("outcome_unknown");
  if(!isCurrent())throw new Error("invalid_session");
  return journal("readwrite",(store,finish)=>{
    const request=store.get(entry.key);
    request.onsuccess=()=>{
      if(!isCurrent()){store.transaction.abort();return;}
      const previous=request.result;
      if(!previous||!validateFileEditJournal(previous,entry.ownerId,entry.authSessionId)||JSON.stringify(previous.body)!==JSON.stringify(entry.body)){store.transaction.abort();return;}
      if(remove)store.delete(entry.key);else{const {confirmedResult:_proof,...durable}=entry;store.put({...durable,body:fileEditDurableBody(entry.body),status:"succeeded"});}
      finish(undefined);
    };
  });
}
